import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Map as LeafletMap, LayerGroup, Marker as LeafletMarker, LeafletMouseEvent } from 'leaflet';
import type { AddressData } from '@/services/maps/MapCacheService';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { contactsService, type NearContactDTO } from '@/services/contacts/ContactsService';
import { mapCacheService } from '@/services/maps/MapCacheService';

interface VisitedContactsMapProps {
  isOpen: boolean;
  onClose: () => void;
  initialAddress?: string; // optional: geocode and center on open
}

const statusColor: Record<NearContactDTO['status'], string> = {
  potential: '#3b82f6', // blue
  active: '#16a34a', // green
  inactive: '#6b7280', // gray
  converted: '#a855f7', // purple
  absent: '#f59e0b', // amber
  refused: '#ef4444', // red
  completed: '#10b981', // teal
  dropped: '#f97316', // orange
  abandoned: '#9ca3af', // neutral gray
};

const statusLabelPT: Record<NearContactDTO['status'], string> = {
  potential: 'Potencial',
  active: 'Em estudo',
  inactive: 'Inativo',
  converted: 'Batizado',
  absent: 'Ausente',
  refused: 'Recusou',
  completed: 'Concluído',
  abandoned: 'Abandonou',
  dropped: 'Desistiu',
};

const VisitedContactsMap: React.FC<VisitedContactsMapProps> = ({ isOpen, onClose, initialAddress }) => {
  const mapRef = useRef<LeafletMap | null>(null);
  const markersLayerRef = useRef<LayerGroup | null>(null);
  const mapElRef = useRef<HTMLDivElement>(null);
  const selectedMarkerRef = useRef<LeafletMarker | null>(null);

  const [loading, setLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [sameStreet, setSameStreet] = useState(true);
  const [street, setStreet] = useState<string>('');
  const [radius, setRadius] = useState<number>(300);
  const [contacts, setContacts] = useState<NearContactDTO[]>([]);

  // Helper: extract a street-like value from an AddressData
  const extractStreet = useCallback((addr: AddressData | null | undefined): string => {
    if (!addr) return '';
    // Prefer 'road'; otherwise empty (we want street name only for backend filter)
    return (addr.road || '').toString();
  }, []);

  // Reverse geocode and set street from current coords
  const syncStreetFromPosition = useCallback(async (lat: number, lng: number) => {
    // Try cached first
    const cached = mapCacheService.findCachedAddress(lat, lng);
    let streetName = extractStreet(cached);
    if (!streetName && navigator.onLine) {
      const fetched = await mapCacheService.preloadAddressForLocation(lat, lng);
      streetName = extractStreet(fetched || undefined);
    }
    if (streetName) setStreet(streetName);
  }, [extractStreet]);

  // Initialize Leaflet map
  useEffect(() => {
    if (!isOpen || !mapElRef.current) return;

    let mounted = true;
    let handleMapClick: ((ev: LeafletMouseEvent) => void) | null = null;

    const init = async () => {
      const L = await import('leaflet');

      if (!mounted || !mapElRef.current) return;

      // Default center if geolocation fails
      const center = position ? [position.lat, position.lng] : [-15.793889, -47.882778]; // Brasília

      mapRef.current = L.map(mapElRef.current, {
        zoomControl: true,
        dragging: true,
        scrollWheelZoom: true,
      }).setView(center as [number, number], 16);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(mapRef.current);

      markersLayerRef.current = L.layerGroup().addTo(mapRef.current);

      // Allow selecting a custom position by clicking on the map
      handleMapClick = (ev: LeafletMouseEvent) => {
        const { lat, lng } = ev.latlng;
        setPosition({ lat, lng });
        // Also update street input from this new position (fire-and-forget)
        syncStreetFromPosition(lat, lng);
      };
      mapRef.current.on('click', handleMapClick);
    };

    init();

    return () => {
      mounted = false;
      if (mapRef.current) {
        if (handleMapClick) {
          mapRef.current.off('click', handleMapClick);
        }
        if (selectedMarkerRef.current) {
          selectedMarkerRef.current.remove();
          selectedMarkerRef.current = null;
        }
        mapRef.current.remove();
        mapRef.current = null;
        markersLayerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, syncStreetFromPosition]);

  // Acquire geolocation on open (or center by initialAddress if provided)
  useEffect(() => {
    if (!isOpen) return;
    setGeoError(null);

    const timeout = 8000;
    if (!('geolocation' in navigator)) {
      setGeoError('Geolocation not supported by this browser.');
      return;
    }

    // If an initial address is provided, prefer geocoding it once
    const tryInitialAddress = async (): Promise<boolean> => {
      const q = (initialAddress || '').trim();
      if (!q) return false;
      try {
        const res = await mapCacheService.geocodeAddress(q);
        if (res && Number.isFinite(res.lat) && Number.isFinite(res.lng)) {
          const coords = { lat: res.lat, lng: res.lng };
          setPosition(coords);
          if (res.address?.road) setStreet(res.address.road);
          if (mapRef.current) {
            mapRef.current.setView([coords.lat, coords.lng], 17);
          }
          return true;
        }
      } catch (_) {
        // ignore failures and fallback to geolocation
      }
      return false;
    };

    (async () => {
      const handled = await tryInitialAddress();
      if (handled) return;
      navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPosition(coords);

        // Try detect street from cache if available
        const cached = mapCacheService.findCachedAddress(coords.lat, coords.lng);
        if (cached?.road) setStreet(cached.road);

        // Center map if already initialized
        if (mapRef.current) {
          mapRef.current.setView([coords.lat, coords.lng], 17);
        }
        // If no cached street, attempt to fetch it and populate the field
        if (!cached?.road) {
          syncStreetFromPosition(coords.lat, coords.lng);
        }
      },
      (err) => {
        const msg = err?.message || 'Failed to get current location.';
        setGeoError(msg);
      },
      { enableHighAccuracy: true, timeout, maximumAge: 10000 }
    );
    })();
  }, [isOpen, syncStreetFromPosition, initialAddress]);

  // Keep a draggable marker at the current selected position (initially geolocation)
  useEffect(() => {
    if (!isOpen || !mapRef.current || !position) return;
    let cancelled = false;
    (async () => {
      const L = await import('leaflet');
      if (cancelled) return;
      if (!selectedMarkerRef.current) {
        selectedMarkerRef.current = L.marker([position.lat, position.lng], { draggable: true, title: 'Selected position' });
        selectedMarkerRef.current.on('dragend', () => {
          const ll = selectedMarkerRef.current!.getLatLng();
          setPosition({ lat: ll.lat, lng: ll.lng });
          // Update street based on dragged location
          syncStreetFromPosition(ll.lat, ll.lng);
        });
        selectedMarkerRef.current.addTo(mapRef.current!);
      } else {
        selectedMarkerRef.current.setLatLng([position.lat, position.lng]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, position, syncStreetFromPosition]);

  const clearMarkers = useCallback(() => {
    if (markersLayerRef.current) {
      // Leaflet LayerGroup API
      markersLayerRef.current.clearLayers();
    }
  }, []);

  const drawMarkers = useCallback(async () => {
    if (!mapRef.current) return;

    clearMarkers();

    const L = await import('leaflet');
    let drawn = 0;
    const toGeocode: Array<{ id: number; address: string }> = [];
    contacts.forEach((c) => {
      const lat = Number(c.lat);
      const lng = Number(c.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        if (c.address && c.address.trim()) {
          toGeocode.push({ id: c.id, address: c.address });
        }
        return;
      }
      const color = statusColor[c.status] || '#2563eb';
      const marker = L.circleMarker([lat, lng], {
        radius: 8,
        color,
        weight: 2,
        fillColor: color,
        fillOpacity: 0.6,
      });
      const streetText = c.street ? `<div><b>Street:</b> ${c.street}</div>` : '';
      const addrText = c.address ? `<div>${c.address}</div>` : '';
      marker.bindPopup(
        `<div style="min-width:180px">
          <div><b>${c.name || 'Contact'}</b></div>
          <div>Status: ${c.status}</div>
          ${streetText}
          ${addrText}
          <div><small>${Math.round(c.distance_m)} m</small></div>
        </div>`
      );
      if (markersLayerRef.current) {
        marker.addTo(markersLayerRef.current);
      } else if (mapRef.current) {
        marker.addTo(mapRef.current);
      }
      drawn++;
    });
    // Debug: log how many markers were drawn (non-fatal in production)
    if (typeof console !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.debug('[VisitedContactsMap] markers drawn:', drawn, 'of', contacts.length);
    }

    // Attempt to geocode missing coordinates (lightweight, capped)
    if (toGeocode.length > 0) {
      const sample = toGeocode.slice(0, 10); // cap to avoid excessive requests
      for (const item of sample) {
        try {
          const res = await mapCacheService.geocodeAddress(item.address);
          if (res && Number.isFinite(res.lat) && Number.isFinite(res.lng)) {
            const color = '#2563eb';
            const marker = L.circleMarker([res.lat, res.lng], {
              radius: 8,
              color,
              weight: 2,
              fillColor: color,
              fillOpacity: 0.6,
            });
            marker.bindPopup(`<div style="min-width:180px"><div><b>Contato</b></div><div>${item.address}</div></div>`);
            if (markersLayerRef.current) {
              marker.addTo(markersLayerRef.current);
            } else if (mapRef.current) {
              marker.addTo(mapRef.current);
            }
          }
        } catch (_) {
          // ignore per-item geocode errors
        }
      }
    }
  }, [contacts, clearMarkers]);

  useEffect(() => {
    if (!isOpen) return;
    drawMarkers();
  }, [contacts, isOpen, drawMarkers]);

  const fetchContacts = useCallback(async () => {
    if (!position) return;
    try {
      setLoading(true);
      const streetOnly = street ? street.split(',')[0].trim() : null;
      // Only enforce same_street when we actually have a street string
      const useSameStreet = sameStreet && !!streetOnly;
      const radii = useSameStreet ? [radius, 500, 800] : [radius];
      let found: NearContactDTO[] = [];
      for (const r of radii) {
        const res = await contactsService.listNear({
          lat: position.lat,
          lng: position.lng,
          radius: r,
          same_street: useSameStreet,
          street: useSameStreet ? streetOnly : null,
        });
        found = res.data || [];
        if (found.length >= 3 || r === radii[radii.length - 1]) {
          break;
        }
      }

      // Fallback: if same_street is active and too few results, try fetching without same_street and filter by street locally
      if (useSameStreet && (found.length < 6) && streetOnly) {
        try {
          const alt = await contactsService.listNear({
            lat: position.lat,
            lng: position.lng,
            radius: Math.max(...radii),
            same_street: false,
            street: null,
          });

          const normalize = (s: string) => s
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .replace(/\s+/g, ' ')
            .trim();
          const needle = normalize(streetOnly);

          const extra = (alt.data || []).filter(c => c.street && normalize(c.street).includes(needle));
          const all = [...found, ...extra];
          // De-duplicate by id
          const seen = new Set<number>();
          found = all.filter(c => {
            if (seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
        } catch (_) {
          // ignore fallback errors
        }
      }
      setContacts(found);
      // Fit bounds to markers if any
      if (found.length > 0) {
        const L = await import('leaflet');
        const latlngs: [number, number][] = found.map((c) => [c.lat, c.lng]);
        const bounds = L.latLngBounds(latlngs);
        mapRef.current?.fitBounds(bounds.pad(0.2));
      }
    } catch (e) {
      console.warn('Failed to load nearby contacts', e);
    } finally {
      setLoading(false);
    }
  }, [position, radius, sameStreet, street]);

  // Geocode typed street to coordinates and move the map/position
  const goToStreet = useCallback(async () => {
    const q = (street || '').trim();
    if (!q) return;
    try {
      const result = await mapCacheService.geocodeAddress(q);
      if (result && Number.isFinite(result.lat) && Number.isFinite(result.lng)) {
        const coords = { lat: result.lat, lng: result.lng };
        setPosition(coords);
        if (mapRef.current) {
          mapRef.current.setView([coords.lat, coords.lng], 17);
        }
        // After moving, refresh contacts with new center
        await fetchContacts();
      }
    } catch (_) {
      // swallow geocode errors; UI remains unchanged
    }
  }, [street, fetchContacts]);

  // Auto-fetch once we have a position
  useEffect(() => {
    if (!isOpen || !position) return;
    fetchContacts();
  }, [isOpen, position, fetchContacts]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mapa de Contatos Visitados">
      <div className="space-y-3">
        {geoError && (
          <div className="text-sm text-red-600">
            {geoError} — autorize o acesso à localização para filtrar pela sua rua atual.
          </div>
        )}

        {/* Linha de controles 1 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Raio (metros)</label>
            <select
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm"
            >
              <option value={150}>150</option>
              <option value={300}>300</option>
              <option value={500}>500</option>
              <option value={800}>800</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="same-street"
              type="checkbox"
              checked={sameStreet}
              onChange={(e) => setSameStreet(e.target.checked)}
            />
            <label htmlFor="same-street" className="text-sm">Apenas mesma rua</label>
          </div>
        </div>

        {/* Linha de controles 2: rua */}
        <div className="flex gap-2">
          <input
            type="text"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void goToStreet();
              }
            }}
            placeholder="Nome da rua"
            disabled={!sameStreet}
            className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm disabled:bg-gray-100"
          />
          <Button onClick={goToStreet} disabled={loading || !sameStreet}>
            Ir
          </Button>
          <Button onClick={fetchContacts} disabled={loading || !position}>
            {loading ? 'Carregando…' : 'Atualizar'}
          </Button>
        </div>

        {/* Map area */}
        <div ref={mapElRef} className="w-full h-[60vh] rounded-md overflow-hidden border border-gray-200" />

        {/* Legenda */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-xs mt-2">
          {Object.entries(statusColor).map(([status, color]) => (
            <div key={status} className="flex items-center gap-2">
              <span
                style={{ background: color }}
                className="inline-block w-3 h-3 rounded-full border border-black/10"
              />
              <span className="capitalize">{statusLabelPT[status as NearContactDTO['status']] ?? status}</span>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </Modal>
  );
};

export default VisitedContactsMap;
